import { normalizeHeader, parseCsv, serializeCsv } from './csvUtils';

/**
 * Column positions the assistant needs from the bulk-edit export. Resolved by
 * header name at runtime rather than by fixed index so that adding or
 * reordering columns in the export does not silently write to the wrong field.
 */
export interface BulkEditColumns {
  locationId: number;
  lastModified: number;
  description: number;
  locationName: number;
  /** Index of every column in the export, keyed by normalized header name. */
  indexByColumn: Record<string, number>;
}

export interface ParsedBulkEditExport {
  header: string[];
  /** Data rows only (the header row is held separately). */
  rows: string[][];
  columns: BulkEditColumns;
}

/**
 * Returns the index of the first header matching any candidate, in candidate
 * order — so an exact "location_description" wins over a looser fallback.
 */
const findColumn = (normalized: string[], candidates: string[]): number => {
  for (const candidate of candidates) {
    const index = normalized.indexOf(candidate);
    if (index !== -1) return index;
  }
  return -1;
};

export class BulkEditFormatError extends Error {}

/**
 * Parses the bulk-edit export CSV and locates the columns the assistant needs.
 *
 * @throws BulkEditFormatError when a required column is absent, rather than
 *   guessing — writing a value into the wrong column would corrupt live listings.
 */
export const parseBulkEditExport = (csvText: string): ParsedBulkEditExport => {
  const parsed = parseCsv(csvText);
  if (parsed.length === 0) {
    throw new BulkEditFormatError('The locations export came back empty.');
  }

  const [header, ...rows] = parsed;
  const normalized = header.map(normalizeHeader);

  const indexByColumn: Record<string, number> = {};
  normalized.forEach((name, index) => {
    if (name && !(name in indexByColumn)) indexByColumn[name] = index;
  });

  const columns: BulkEditColumns = {
    locationId: findColumn(normalized, ['location_id', 'id']),
    lastModified: findColumn(normalized, ['last_modified_date', 'last_modified', 'lastmodifieddate']),
    description: findColumn(normalized, ['location_description', 'description']),
    locationName: findColumn(normalized, ['location_name', 'name']),
    indexByColumn,
  };

  if (columns.locationId === -1) {
    throw new BulkEditFormatError('The locations export has no location_id column.');
  }
  if (columns.description === -1) {
    throw new BulkEditFormatError('The locations export has no description column.');
  }

  // Drop blank trailing lines produced by the final newline.
  const dataRows = rows.filter((row) => row.some((cell) => cell.trim() !== ''));

  return { header, rows: dataRows, columns };
};

/** Reads a cell defensively — short rows are possible in hand-edited CSVs. */
export const cellAt = (row: string[], index: number): string =>
  index >= 0 && index < row.length ? row[index] : '';

/** New values for one location, keyed by CSV column name. */
export type RowUpdate = Record<string, string>;

/**
 * Rebuilds the upload CSV from the export.
 *
 * Every exported row is included so the file stays a complete picture, but each
 * row is emptied except for `location_id`, `last_modified_date` and the approved
 * new values. The export's contract is that a blank cell keeps the current
 * value, so untouched rows are no-ops and the server-side preview reports only
 * the fields that actually change.
 *
 * `last_modified_date` is carried through untouched because the server uses it
 * to reject edits built from a stale export.
 *
 * @param source Parsed export the values were derived from
 * @param updates New values keyed by location id, then by CSV column name
 * @throws BulkEditFormatError when a requested column is not in the export
 */
export const buildBulkEditCsv = (
  source: ParsedBulkEditExport,
  updates: Map<string, RowUpdate>,
): string => {
  const { header, rows, columns } = source;

  const outputRows = rows.map((row) => {
    const blank = new Array<string>(header.length).fill('');
    const locationId = cellAt(row, columns.locationId);

    blank[columns.locationId] = locationId;
    if (columns.lastModified !== -1) {
      blank[columns.lastModified] = cellAt(row, columns.lastModified);
    }

    const update = updates.get(locationId);
    if (update) {
      Object.entries(update).forEach(([column, value]) => {
        const index = columns.indexByColumn[column];
        if (index === undefined) {
          throw new BulkEditFormatError(`The locations export has no "${column}" column.`);
        }
        blank[index] = value;
      });
    }

    return blank;
  });

  return serializeCsv([header, ...outputRows]);
};

/** Wraps CSV text as a File so it can be posted to the existing upload endpoints. */
export const csvToFile = (csvText: string, fileName = 'assistant-location-edit.csv'): File =>
  new File([csvText], fileName, { type: 'text/csv' });
